/**
 * Reusable specs-accordion
 * ------------------------------------------------------------------
 * Markup:
 *   <div class="specs-accordion" data-specs-accordion>      (optional wrapper — scopes "close others")
 *     <div class="specs-accordion-item">
 *       <button class="specs-accordion-header" data-target="PANEL_ID" aria-controls="PANEL_ID" aria-expanded="false">
 *         <span>Title</span>
 *         <span class="specs-toggle" aria-hidden="true"></span>
 *       </button>
 *       <div class="specs-accordion-panel" id="PANEL_ID">…</div>
 *     </div>
 *   </div>
 *
 * Behaviour: single panel open at a time within its [data-specs-accordion] scope
 * (or page-wide if no wrapper). Add `data-allow-multiple` on the wrapper to keep
 * several open at once. Uses event delegation, so accordions added later
 * (e.g. in the Shopify theme editor) work without re-initialising.
 */
(function () {
  function panelFor(header) {
    if (header.dataset.target) {
      var byId = document.getElementById(header.dataset.target);
      if (byId) return byId;
    }
    var item = header.closest('.specs-accordion-item') || header.parentElement;
    return item ? item.querySelector('.specs-accordion-panel') : null;
  }

  function setState(header, panel, open) {
    header.classList.toggle('specs-active', open);
    header.setAttribute('aria-expanded', open ? 'true' : 'false');

    var toggle = header.querySelector('.specs-toggle');
    if (toggle) toggle.classList.toggle('specs-active', open);

    if (panel) panel.classList.toggle('open', open);
  }

  function toggle(header) {
    var panel = panelFor(header);

    if (header.classList.contains('specs-active')) {
      setState(header, panel, false);
      return;
    }

    var scope = header.closest('[data-specs-accordion]') || document;
    var allowMultiple = scope !== document && scope.hasAttribute('data-allow-multiple');

    if (!allowMultiple) {
      scope.querySelectorAll('.specs-accordion-header.specs-active').forEach(function (other) {
        if (other !== header) setState(other, panelFor(other), false);
      });
    }

    setState(header, panel, true);
  }

  // Click (also fires for keyboard activation of a <button> header).
  document.addEventListener('click', function (e) {
    var header = e.target.closest('.specs-accordion-header');
    if (header) toggle(header);
  });

  /**
   * Simple tabs (delegated, survives product re-renders)
   * ------------------------------------------------------------------
   * <div data-tabs>
   *   <button data-tab-button="0" class="is-active">…</button>
   *   <div data-tab-panel="0" class="is-active">…</div>
   * </div>
   */
  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-tab-button]');
    if (!button) return;

    var root = button.closest('[data-tabs]');
    if (!root) return;

    var index = button.getAttribute('data-tab-button');

    root.querySelectorAll('[data-tab-button]').forEach(function (btn) {
      var active = btn.getAttribute('data-tab-button') === index;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    root.querySelectorAll('[data-tab-panel]').forEach(function (panel) {
      panel.classList.toggle('is-active', panel.getAttribute('data-tab-panel') === index);
    });
  });

  // Keyboard support for non-button headers (e.g. a div with role="button").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var header = e.target.closest('.specs-accordion-header');
    if (!header || header.tagName === 'BUTTON' || header.tagName === 'A') return;
    e.preventDefault();
    toggle(header);
  });
})();





async function applySellingPlan({ lineKey, sellingPlanId, quantity = 1, btn = null }) {
  if (!lineKey || !sellingPlanId) return;

  const root = window.Shopify?.routes?.root || "/";

  if (btn) {
    btn.classList.add("loading_tr");
    btn.disabled = true;
  }

  try {
    // Get the current cart so we can convert line item key -> line number
    const cartRes = await fetch(`${root}cart.js`);
    const cart = await cartRes.json();

    const lineIndex = cart.items.findIndex(item => item.key === lineKey);

    if (lineIndex === -1) {
      throw new Error(`Line item not found for key: ${lineKey}`);
    }

    const response = await fetch(`${root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        line: lineIndex + 1,
        quantity: Number(quantity) || 1,
        selling_plan: Number(sellingPlanId)
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.description || data?.message || `Cart change failed: ${response.status}`);
    }

    const cartDrawerSlide = document.querySelector("#cart-drawer");
    if (cartDrawerSlide && cartDrawerSlide._onCartRefresh) {
      await cartDrawerSlide._onCartRefresh();
      cartDrawerSlide.show();
    }

    if (btn) btn.style.display = "none";
  } catch (error) {
    console.error("Failed to apply selling plan:", error);
  } finally {
    if (btn) {
      btn.classList.remove("loading_tr");
      btn.disabled = false;
    }
  }
}

document.addEventListener("click", function (e) {
  const btn = e.target.closest(".subscribe-btn");
  if (!btn) return;

  e.preventDefault();

  applySellingPlan({
    lineKey: btn.dataset.lineKey,
    sellingPlanId: btn.dataset.sellingPlanId,
    quantity: btn.dataset.quantity,
    btn
  });
});








/**
 * Upsell bundle
 * ------------------------------------------------------------------
 * Powers the "complete your set" card (snippets/product-upsell.liquid): keeps the live total in
 * sync with the checkboxes, and adds the current product plus every checked upsell to the cart in
 * one request. Reuses the theme's cart events so the drawer opens and refreshes exactly as it does
 * for a normal add to cart.
 *
 * The displayed discount is presentational only — it is NOT applied to the cart. Apply the real
 * saving with a Shopify automatic/bundle discount.
 */
(function () {
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    const match = fmt.match(placeholder);
    let value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  class UpsellBundle extends HTMLElement {
    connectedCallback() {
      this.discount = parseFloat(this.dataset.discount) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.compareEl = this.querySelector('[data-bundle-compare]');
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.checks = Array.from(this.querySelectorAll('[data-bundle-check]'));
      this.addBtn = this.querySelector('[data-bundle-add]');

      this.checks.forEach((c) =>
        c.addEventListener('change', () => {
          this._syncToggle();
          this._updateTotal();
        })
      );

      if (this.toggle) {
        this.toggle.addEventListener('change', () => {
          this.checks.forEach((c) => {
            c.checked = this.toggle.checked;
          });
          this._updateTotal();
        });
      }

      if (this.addBtn) this.addBtn.addEventListener('click', () => this._addBundle());

      // The upsell block is not in the theme's partial-rerender allowlist, so it is not refreshed
      // when the shopper switches variant. Track variant:change to keep the main line current.
      this._onVariantChange = this._handleVariantChange.bind(this);
      document.addEventListener('variant:change', this._onVariantChange);

      this._updateTotal();
    }

    disconnectedCallback() {
      if (this._onVariantChange) document.removeEventListener('variant:change', this._onVariantChange);
    }

    get form() {
      return document.getElementById(this.dataset.form);
    }

    _handleVariantChange(event) {
      const detail = event.detail || {};
      if (detail.formId !== this.dataset.form || !detail.variant) return;

      const variant = detail.variant;
      this.dataset.mainVariant = variant.id;

      const lockedRow = this.querySelector('[data-locked]');
      if (lockedRow && typeof variant.price === 'number') lockedRow.dataset.price = variant.price;

      if (this.addBtn) {
        const available = variant.available !== false;
        this.toggleAttribute('data-unavailable', !available);
        this.addBtn.disabled = !available;
      }

      this._updateTotal();
    }

    _syncToggle() {
      if (this.toggle) this.toggle.checked = this.checks.every((c) => c.checked);
    }

    _updateTotal() {
      let sum = 0;
      this.querySelectorAll('[data-bundle-item]').forEach((row) => {
        const price = parseInt(row.dataset.price, 10) || 0;
        const check = row.querySelector('[data-bundle-check]');
        if (!check || check.checked) sum += price;
      });

      let total = sum;
      if (this.discount > 0) {
        total = Math.round((sum * (100 - this.discount)) / 100);
        if (this.compareEl) this.compareEl.innerHTML = formatMoney(sum, this.moneyFormat);
      }
      if (this.totalEl) this.totalEl.innerHTML = formatMoney(total, this.moneyFormat);
    }

    _collectItems() {
      const items = [];
      const form = this.form;
      const data = form ? new FormData(form) : null;

      const mainId = (data && data.get('id')) || this.dataset.mainVariant;
      const qty = (data && parseInt(data.get('quantity'), 10)) || 1;
      if (mainId) items.push({ id: mainId, quantity: qty });

      this.querySelectorAll('[data-bundle-item]').forEach((row) => {
        const vid = row.dataset.variantId;
        const check = row.querySelector('[data-bundle-check]');
        if (vid && check && check.checked) items.push({ id: vid, quantity: 1 });
      });

      return items;
    }

    async _addBundle() {
      if (this.hasAttribute('data-unavailable')) return;

      const items = this._collectItems();
      if (!items.length) return;

      this.addBtn.setAttribute('aria-busy', 'true');

      try {
        // "variant-added" mirrors the theme so the popover cart notification also works; the drawer
        // adds its own section id through this event.
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
        );

        const root = window.Shopify.routes.root;
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: Array.from(new Set(sections)).join(','),
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Add to cart failed');

        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = result.sections;

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: result.items || [result], cart },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
        );
      } catch (error) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        console.error('[upsell-bundle]', error);
      } finally {
        this.addBtn.removeAttribute('aria-busy');
      }
    }
  }

  if (!window.customElements.get('upsell-bundle')) {
    window.customElements.define('upsell-bundle', UpsellBundle);
  }
})();
